import { useMemo, useState } from 'react';
import clsx from 'clsx';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useBrainStore } from '@/state/brainStore';
import { useSimulationStore } from '@/state/simulationStore';
import { buildGlance } from '@/utils/conversationGlance';
import ConversationThread from '@/components/ConversationThread';
import type { CustomerRecord } from '@/domain/types';

interface Props {
  customer: CustomerRecord;
}

/**
 * Six facts instead of a transcript: what they want, how cold the thread has
 * gone, what money is outstanding, which channel to use, and their own last
 * words. Every line is a lookup over the record - see utils/conversationGlance.
 *
 * The full thread stays one click away rather than going anywhere.
 */
export default function ConversationGlance({ customer }: Props) {
  const bookings = useBrainStore((s) => s.bookings);
  const deposits = useBrainStore((s) => s.deposits);
  const pieces = useBrainStore((s) => s.pieces);
  const nowIso = useSimulationStore((s) => s.clockIso);
  const summary = useBrainStore((s) => s.sequenceState[customer.id]?.description);
  const [threadOpen, setThreadOpen] = useState(false);

  const glance = useMemo(
    () => buildGlance({ customer, bookings, deposits, pieces, nowIso, summary }),
    [customer, bookings, deposits, pieces, nowIso, summary],
  );

  return (
    <div className="space-y-3">
      <section
        className="rounded-lg border border-gold-dim/25 border-l-2 border-l-gold-bright bg-ink-black px-4 py-3"
        data-testid="qwm-glance"
      >
        <p className="label-small-caps text-gold-bright">At a glance</p>
        <dl className="mt-3 grid grid-cols-[104px_1fr] gap-x-3 gap-y-1.5">
          {glance.rows.map((row) => (
            <div key={row.key} className="contents">
              <dt className="font-inter text-[10px] uppercase tracking-widest text-cream-faint pt-1">
                {row.label}
              </dt>
              <dd
                className={clsx(
                  'font-serif text-sm m-0',
                  row.tone === 'urgent'
                    ? 'text-blood'
                    : row.tone === 'good'
                      ? 'text-mint'
                      : 'text-cream',
                )}
                data-testid={`qwm-glance-${row.key}`}
              >
                {row.value}
              </dd>
            </div>
          ))}
        </dl>
        {glance.quote ? (
          <blockquote
            className="mt-3 rounded-md bg-ink-dark px-3 py-2.5"
            data-testid="qwm-glance-quote"
          >
            <p className="font-serif text-sm text-cream m-0">&ldquo;{glance.quote.body}&rdquo;</p>
            <cite className="block mt-1.5 font-inter text-[10px] not-italic uppercase tracking-widest text-cream-faint">
              {glance.quote.attribution}
            </cite>
          </blockquote>
        ) : null}
      </section>

      {glance.messageCount > 0 ? (
        <button
          type="button"
          onClick={() => setThreadOpen((v) => !v)}
          aria-expanded={threadOpen}
          className="inline-flex items-center gap-1.5 rounded-md border border-dashed border-azure/40 bg-azure/5 px-3 py-1.5 font-inter text-[11px] text-azure hover:border-azure/70 transition"
          data-testid="qwm-thread-toggle"
        >
          {threadOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
          {threadOpen
            ? 'Hide full conversation'
            : `Show full conversation (${glance.messageCount} ${glance.messageCount === 1 ? 'message' : 'messages'})`}
        </button>
      ) : null}

      {threadOpen ? <ConversationThread customer={customer} /> : null}
    </div>
  );
}
